import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const GDocTag = () => (
  <span data-testid="gdoc-tag" className="inline-flex items-center gap-1 rounded-full border border-[#E3E7EE] bg-[#F5F8FF] px-1.5 py-0.5 text-[10px] text-[#1a73e8] font-normal shrink-0">
    <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16zm0-6h4v1.5H8V10z" /></svg>Google Doc
  </span>
);

export const openGDoc = (d) => window.open(d.google_url, "_blank", "noopener,noreferrer");

export function GoogleDocEditDialog({ doc, categories = [], onClose, onSaved }) {
  const [f, setF] = useState({ name: "", url: "", category: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (doc) setF({ name: doc.name || "", url: doc.google_url || "", category: doc.category || "" }); }, [doc]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/console/docs/${doc.id}/google`, { name: f.name, url: f.url, category: f.category || null }); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!(await confirmDelete({ message: `Remove “${doc.name}” from the doc table? The Google Doc itself isn't touched.`, confirmText: "Remove" }))) return;
    try { await api.delete(`/console/docs/${doc.id}/google`); toast.success("Removed"); onSaved(); onClose(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] sm:max-w-md" data-testid="gdoc-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Imported Google Doc</DialogTitle><DialogDescription>{doc?.client}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="gdoc-edit-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Doc name" className="bg-[#F6F6FD] border-[#EEEFEF]" />
          <Input data-testid="gdoc-edit-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://docs.google.com/…" className="bg-[#F6F6FD] border-[#EEEFEF]" />
          <Select value={f.category || "__none"} onValueChange={(v) => setF({ ...f, category: v === "__none" ? "" : v })}>
            <SelectTrigger data-testid="gdoc-edit-category" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="__none">No category</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2 pt-1">
            <Button data-testid="gdoc-edit-remove" variant="ghost" onClick={remove} className="text-rose-600 hover:text-rose-700 rounded-full mr-auto">Remove</Button>
            <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button data-testid="gdoc-edit-save" onClick={save} disabled={busy || !f.name.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
